import "./style.scss";
import { DOMAIN, ADD_ONS } from "constants/common";
import { getAmenities } from "src/utils/amenities";
import GPSIcon from "icons/gps.svg";
import { Button, Container, Typography } from "components/ct-design";
import dayjs from "dayjs";
import { FunctionComponent, useContext, useState } from "react";
import StarIcon from "icons/star.svg";
import { SelectedTabType, TuppleProps } from "./types";
import { ResultsContext } from "src/context/resultsContext/context";
import { ChartContext } from "src/context/chartsContext/chartContext/context";
import BusInfo from "src/containers/results/busInfo";
import { sendBusUIAction } from "src/analytics/useAnalytics";
import useGetChartData from "./customHooks/useGetChartData";
import { getMessageFromObject } from "./utils";

const Tupple: FunctionComponent<TuppleProps> = (props) => {
  const {
    data: {
      deptTime,
      arrTime,
      availableSeats,
      fares,
      review,
      duration,
      meta,
      couponData,
      solutionId,
    },
    index,
    children,
    id,
    setShowToast,
    isRTC,
    isSEOPage = false,
  } = props;

  const { resultsData, showSeatLayout } = useContext(ResultsContext);
  const { loading, chartData } = useContext(ChartContext);
  const [showSideSheet, setShowSideSheet] = useState(false);
  const [selectedTab, setSelectedTab] = useState<SelectedTabType>("0");
  const { handleSeatChart } = useGetChartData(setShowToast);

  const handleSideSheet = (tab: SelectedTabType) => {
    setShowSideSheet((prev) => !prev);
    setSelectedTab(tab);
  };

  const addOns = resultsData?.addOns?.[ADD_ONS.BUS_PASS];

  const showSRPCouponCallout =
    couponData &&
    couponData.discount > 0 &&
    couponData.discountedPrice > 0 &&
    couponData.discountedPrice < fares[0].total;

  const showAmen = () => (
    <div className="flex flex-middle">
      {meta.amenities
        .filter((item) => item.toLowerCase() !== "gps")
        .map((el, index) => {
          if (index <= 3)
            return (
              <div className="h-6" key={el}>
                {getAmenities(el)}
              </div>
            );
        })}
    </div>
  );

  const triggerBusUIAction = () => {
    const dx = Math.ceil(
      dayjs(resultsData?.sc?.journeyDate).diff(dayjs(), "day", true)
    );
    sendBusUIAction(
      {
        actionName: "srpClicked",
        isRTCBus: isRTC || false,
        busRating: review?.rating || "NA",
        numberOfRatings: review?.ratingCount || 0,
        numberOfSeatsLeft: availableSeats || "NA",
        pricePerSeat: fares[0]?.baseDrivenFare?.total || "NA",
        departureTime: dayjs(deptTime).format("h:mm A"),
        arrivalTime: dayjs(arrTime).format("h:mm A"),
        travelDuration: `${duration.split(":")[0]}h ${duration.split(":")[1]}m`,
        amenitiesShown: meta?.amenities?.length ? "yes" : "no",
        operatorName: meta?.operatorName || "NA",
        busType: meta?.displayBusType || "NA",
        srpRanking: index || 0,
        sourceCity: resultsData?.sc?.fromCityName,
        destinationCity: resultsData?.sc?.toCityName,
        travelDate: resultsData?.sc?.journeyDate,
        searchId: resultsData?.sc?.searchId,
        dx,
      },
      "b_srp_tuple_click"
    );
  };

  const getSeatChart = async () => {
    if (isSEOPage) {
      window.open(
        `${DOMAIN}/bus/chart?fromCity=${resultsData?.sc?.fromCityId}&toCity=${resultsData?.sc?.toCityId}&journeyDate=${resultsData?.sc?.journeyDate}&solutionId=${solutionId}`,
        "_blank"
      );
      return;
    }
    handleSeatChart(solutionId, resultsData?.sc?.searchId, triggerBusUIAction);
  };

  return (
    <>
      <Container
        id={id}
        className="ba-solid bc-line-500 br-12 pb-3 my-4 bg-neutral-100 tupple"
      >
        <Container className="flex flex-between mx-4 mt-4">
          <div>
            <Typography variant="B1">
              {meta.operatorName ? meta.operatorName : ""}
            </Typography>
            <Typography variant="B3" className="mt-1" colorCode="#808080">
              {meta.displayBusType ? meta.displayBusType : ""}
            </Typography>
          </div>

          {(review?.ratingCount || 0) && review?.rating > 0 ? (
            <div className="flex flex-column ta-center">
              <Container
                backgroundColor={
                  review.rating > 3.5
                    ? "#11A670"
                    : review.rating < 2.6
                    ? "#c83232"
                    : "#df9641"
                }
                className="rating-val py-1 px-3  flex flex-middle flex-center"
                color="#fff"
              >
                <StarIcon />
                <Typography variant="TAG" colorCode="#fff" className="ml-2px">
                  {review.rating}
                </Typography>
              </Container>
              <Container className="fw-600 py-1 px-2 rating-count">
                <Typography variant="TAG" colorCode="#808080">
                  {review?.ratingCount || 0}
                </Typography>
              </Container>
            </div>
          ) : null}
        </Container>

        <div className="flex flex-between mx-4 mt-6">
          <div className="flex flex-start h-7 flex-middle">
            <Container className="flex">
              <p className="fw-500 fs-20 lh-28">
                {dayjs(deptTime).format("h:mm A")}
              </p>
            </Container>

            <Container
              className="mx-6 p-relative"
              css={{ marginTop: "2px", marginBottom: "2px" }}
            >
              <Container
                backgroundColor="#f3f3f3"
                className="br-4 py-1 px-4 z-10 p-relative h-6"
              >
                {`${duration.split(":")[0]}h`} {`${duration.split(":")[1]}m`}
              </Container>

              <Container
                className="p-absolute w-100p"
                css={{
                  borderTop: "1px dotted #e6e6e6",
                  width: "120%",
                  left: "-9px",
                  top: "12px",
                }}
              />
            </Container>

            <Container className="flex">
              <p className="fw-500 fs-20 lh-28">
                {dayjs(arrTime).format("h:mm A")}
              </p>
            </Container>
          </div>

          <div className="flex">
            <div className="flex flex-column">
              <div className="flex flex-end flex-baseline">
                <Typography
                  variant="B3"
                  className="flex flex-baseline "
                  colorCode="#808080"
                >
                  from{" "}
                  {fares[0].discPercentage > 0 ? (
                    <Typography
                      isStriked
                      variant="B3"
                      className="ml-1 fs-14 lh-20"
                      colorCode="#808080"
                    >
                      {`₹${fares[0].baseDrivenFare.strikeThrough.toLocaleString(
                        "en-US"
                      )}`}
                    </Typography>
                  ) : null}
                </Typography>
                <Typography className="ml-1" variant="HM2">
                  {`₹${fares[0].baseDrivenFare.total.toLocaleString("en-US")}`}
                </Typography>
              </div>
              <Typography variant="B3" colorCode="#11A670">
                {showSRPCouponCallout
                  ? getMessageFromObject(couponData.messageV2, couponData)
                  : null}
                {addOns?.message &&
                fares[0]?.baseDrivenFare.total >= addOns?.mov
                  ? `${
                      couponData?.messageV2 ? " + " : ""
                    }${getMessageFromObject(addOns.message, addOns)}`
                  : null}
              </Typography>
            </div>
            <div className="flex flex-column  ml-4">
              <Button
                color={
                  showSeatLayout && chartData?.hasOwnProperty(solutionId)
                    ? "primary"
                    : "secondary"
                }
                minWidth="136px"
                onClick={getSeatChart}
                loading={loading === solutionId}
                variant={
                  showSeatLayout && chartData?.hasOwnProperty(solutionId)
                    ? "outline"
                    : "contained"
                }
              >
                {showSeatLayout && chartData?.hasOwnProperty(solutionId)
                  ? "Hide seat"
                  : "Select seat"}
              </Button>
              <Typography
                variant="B3"
                className="mt-2 ta-center"
                colorCode={availableSeats > 5 ? "#808080" : "#C83232"}
              >
                {availableSeats} seats available
              </Typography>
            </div>
          </div>
        </div>

        <div className="bb-dotted bc-line-500 mx-4 mt-2 mb-3"></div>

        <div className="flex mx-4 flex-between">
          <div className="flex flex-middle">
            {meta.hasGps ? (
              <Container
                className="br-4 flex flex-middle px-2 py-1"
                backgroundColor="#f3f3f3"
              >
                <GPSIcon />
                <Typography variant="P3" className="ml-1">
                  Live tracking
                </Typography>
              </Container>
            ) : null}

            {meta.amenities?.length > 0 ? (
              <>
                <Container className={meta.hasGps ? "ml-2" : ""}>
                  {showAmen()}
                </Container>
                {meta.amenities.length > 4 ? (
                  <Typography
                    variant="P3"
                    isClickable
                    onClick={() => handleSideSheet("0")}
                    className="ml-2 c-pointer"
                  >
                    +
                    {meta.amenities.filter(
                      (item) => item.toLowerCase() !== "gps"
                    ).length - 4}
                  </Typography>
                ) : null}
              </>
            ) : null}
          </div>

          <BusInfo
            key={solutionId}
            solutionId={solutionId}
            busIndex={index}
            showSideSheet={showSideSheet}
            setShowSideSheet={setShowSideSheet}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            handleSideSheet={handleSideSheet}
          />
        </div>

        {children && showSeatLayout ? (
          <Container
            css={{ borderTop: "1px solid #e6e6e6", marginTop: "14px" }}
            className="pt-6"
          >
            {children}
          </Container>
        ) : null}
      </Container>
    </>
  );
};
export default Tupple;
